import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings } from 'lucide-react';
import { 
  WINDOW_TYPES, 
  SHUTTER_STYLES, 
  LOUVRE_SIZES, 
  CONTROL_TYPES, 
  MOUNT_TYPES, 
  FRAME_TYPES,
  MATERIALS 
} from './constants';

const OpeningForm = ({ opening, onChange, onSave, onCancel, isEditing }) => {
  const updateField = (field, value) => {
    onChange({ ...opening, [field]: value });
  };

  // Parse layout string to count B's and T's
  const parseLayout = (layoutStr) => {
    const upperLayout = (layoutStr || '').toUpperCase();
    const bCount = (upperLayout.match(/B/g) || []).length;
    const tCount = (upperLayout.match(/T/g) || []).length;
    return { bCount, tCount };
  };

  const { bCount, tCount } = parseLayout(opening.layout);

  // Update bay post measurement
  const updateBayPost = (index, field, value) => {
    const bayPosts = [...(opening.bay_posts || [])];
    if (!bayPosts[index]) {
      bayPosts[index] = { measurement: '', angle: '' };
    }
    bayPosts[index][field] = value;
    onChange({ ...opening, bay_posts: bayPosts });
  };

  // Update T post position
  const updateTPost = (index, value) => {
    const tPosts = [...(opening.t_posts || [])];
    tPosts[index] = value;
    onChange({ ...opening, t_posts: tPosts });
  };

  // Handle layout change and ensure arrays are properly sized
  const handleLayoutChange = (newLayout) => {
    const { bCount: newBCount, tCount: newTCount } = parseLayout(newLayout);
    
    let bayPosts = [...(opening.bay_posts || [])];
    while (bayPosts.length < newBCount) {
      bayPosts.push({ measurement: '', angle: '' });
    }
    bayPosts = bayPosts.slice(0, newBCount);
    
    let tPosts = [...(opening.t_posts || [])];
    while (tPosts.length < newTCount) {
      tPosts.push('');
    }
    tPosts = tPosts.slice(0, newTCount);
    
    onChange({ 
      ...opening, 
      layout: newLayout.toUpperCase(),
      bay_posts: bayPosts,
      t_posts: tPosts
    });
  };

  return (
    <div className="border rounded-lg p-4 space-y-4 bg-white">
      <h4 className="font-medium flex items-center gap-2">
        <Settings className="h-4 w-4" />
        Opening Details
      </h4>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Room Location *</Label>
          <Input
            value={opening.room_location}
            onChange={(e) => updateField('room_location', e.target.value)}
            placeholder="e.g. Living Room, Master Bedroom"
            data-testid="room-location-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Opening Reference</Label>
          <Input
            value={opening.opening_reference}
            onChange={(e) => updateField('opening_reference', e.target.value)}
            placeholder="e.g. LR-1, MB-W1"
          />
        </div>
      </div>

      {/* Window Type and Material */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Window Type *</Label>
          <Select value={opening.window_type} onValueChange={(v) => updateField('window_type', v)}>
            <SelectTrigger data-testid="window-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Material *</Label>
          <Select value={opening.material || 'paulownia'} onValueChange={(v) => updateField('material', v)}>
            <SelectTrigger data-testid="opening-material-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MATERIALS.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} - £{m.pricePerSqm}/m²
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Window Measurements */}
      <div className="space-y-4 p-4 bg-[#8CB89F]/10 rounded-lg">
        <h5 className="font-medium text-[#2F4F4F]">Window Measurements</h5>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Width (mm) *</Label>
            <Input
              type="number"
              value={opening.width || ''}
              onChange={(e) => updateField('width', e.target.value ? parseFloat(e.target.value) : '')}
              placeholder="1000"
              data-testid="window-width"
            />
          </div>
          <div className="space-y-2">
            <Label>Height (mm) *</Label>
            <Input
              type="number"
              value={opening.height || ''}
              onChange={(e) => updateField('height', e.target.value ? parseFloat(e.target.value) : '')}
              placeholder="1400"
              data-testid="window-height"
            />
          </div>
        </div>
      </div>

      {/* Layout Input */}
      <div className="space-y-2">
        <Label>Layout</Label>
        <p className="text-xs text-gray-500">Enter panel configuration using L (Left), B (Bay Post), T (T-Post), R (Right). Example: LBTR, LBBR, LTBR</p>
        <Input
          value={opening.layout || ''}
          onChange={(e) => handleLayoutChange(e.target.value)}
          placeholder="e.g. LBTR"
          className="uppercase"
          data-testid="layout-input"
        />
      </div>

      {/* Midrail and Split Height */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-[#8CB89F]/10 rounded-lg">
        <div className="space-y-2">
          <Label>Midrail (mm)</Label>
          <p className="text-xs text-gray-500">Height from bottom to midrail</p>
          <Input
            type="number"
            value={opening.midrail || ''}
            onChange={(e) => updateField('midrail', e.target.value ? parseFloat(e.target.value) : '')}
            placeholder="e.g. 800"
            data-testid="midrail-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Split Height (mm)</Label>
          <p className="text-xs text-gray-500">Height where panels split</p>
          <Input
            type="number"
            value={opening.split_height || ''}
            onChange={(e) => updateField('split_height', e.target.value ? parseFloat(e.target.value) : '')}
            placeholder="e.g. 1000"
            data-testid="split-height-input"
          />
        </div>
      </div>

      {/* Dynamic Bay Post Fields */}
      {bCount > 0 && (
        <div className="space-y-4 p-4 bg-green-50 rounded-lg">
          <h5 className="font-medium text-green-800">Bay Post Configuration</h5>
          {Array.from({ length: bCount }, (_, i) => (
            <div key={`bay-${i}`} className="space-y-3 p-3 bg-white rounded border border-green-200">
              <h6 className="font-medium text-green-700">Bay Post {i + 1}</h6>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bay Post {i + 1} Measurement (mm)</Label>
                  <Input
                    type="number"
                    value={opening.bay_posts?.[i]?.measurement || ''}
                    onChange={(e) => updateBayPost(i, 'measurement', e.target.value)}
                    placeholder="Enter measurement"
                    data-testid={`bay-post-${i + 1}-measurement`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bay Post {i + 1} Angle (°)</Label>
                  <Input
                    type="number"
                    value={opening.bay_posts?.[i]?.angle || ''}
                    onChange={(e) => updateBayPost(i, 'angle', e.target.value)}
                    placeholder="Enter angle"
                    data-testid={`bay-post-${i + 1}-angle`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dynamic T Post Fields */}
      {tCount > 0 && (
        <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
          <h5 className="font-medium text-purple-800">T-Post Configuration</h5>
          {Array.from({ length: tCount }, (_, i) => (
            <div key={`tpost-${i}`} className="space-y-2 p-3 bg-white rounded border border-purple-200">
              <Label>T-Post Position {i + 1} (mm)</Label>
              <Input
                type="number"
                value={opening.t_posts?.[i] || ''}
                onChange={(e) => updateTPost(i, e.target.value)}
                placeholder="Enter position measurement"
                data-testid={`t-post-${i + 1}-position`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Shutter Specifications */}
      <div className="space-y-4 pt-4 border-t">
        <h5 className="font-medium">Shutter Specifications</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Shutter Style</Label>
            <Select value={opening.shutter_style} onValueChange={(v) => updateField('shutter_style', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHUTTER_STYLES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Louvre Size</Label>
            <Select value={opening.louvre_size} onValueChange={(v) => updateField('louvre_size', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOUVRE_SIZES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Colour</Label>
            <Input
              value={opening.colour}
              onChange={(e) => updateField('colour', e.target.value)}
              placeholder="White"
            />
          </div>
          <div className="space-y-2">
            <Label>Control Type</Label>
            <Select value={opening.control_type} onValueChange={(v) => updateField('control_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTROL_TYPES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Mount Type</Label>
            <Select value={opening.mount_type} onValueChange={(v) => updateField('mount_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOUNT_TYPES.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Frame Type</Label>
            <Select value={opening.frame_type} onValueChange={(v) => updateField('frame_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FRAME_TYPES.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label>Opening Notes</Label>
        <Textarea
          value={opening.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Any specific notes for this opening..."
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={() => onSave(opening)} 
          disabled={!opening.room_location}
          className="bg-[#8CB89F] hover:bg-[#7aa88f]"
          data-testid="save-opening-btn"
        >
          {isEditing ? 'Update Opening' : 'Save Opening'}
        </Button>
      </div>
    </div>
  );
};

export default OpeningForm;
